/**
 * Analytics Helper Functions
 * Utility functions for data formatting, calculations, and transformations
 */

/**
 * Calculate percentage change between two values
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value
 * @returns {Object} { absolute, percentage, type }
 */
export function calculateChange(current, previous) {
  if (previous === 0) {
    return {
      absolute: current,
      percentage: current > 0 ? 100 : 0,
      type: current > 0 ? 'increase' : 'stable'
    };
  }

  const absolute = current - previous;
  const percentage = (absolute / previous) * 100;
  const type = absolute > 0 ? 'increase' : absolute < 0 ? 'decrease' : 'stable';

  return {
    absolute,
    percentage: parseFloat(percentage.toFixed(2)),
    type
  };
}

/**
 * Format large numbers with K/M/B suffixes
 * @param {number} num - Number to format
 * @param {number} decimals - Decimal places (default: 1)
 * @returns {string} Formatted number
 */
export function formatNumber(num, decimals = 1) {
  if (num === null || num === undefined || isNaN(num)) return '0';

  const absNum = Math.abs(num);

  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(decimals) + 'B';
  }
  if (absNum >= 1e6) {
    return (num / 1e6).toFixed(decimals) + 'M';
  }
  if (absNum >= 1e3) {
    return (num / 1e3).toFixed(decimals) + 'K';
  }

  return num.toLocaleString();
}

/**
 * Format duration in seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export function formatDuration(seconds) {
  if (!seconds || seconds === 0) return '0s';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}

/**
 * Format percentage value
 * @param {number} value - Percentage value (0-1 or 0-100)
 * @param {number} decimals - Decimal places (default: 1)
 * @param {boolean} isDecimal - Whether value is in decimal form (default: true)
 * @returns {string} Formatted percentage
 */
export function formatPercentage(value, decimals = 1, isDecimal = true) {
  if (value === null || value === undefined || isNaN(value)) return '0%';

  const percentage = isDecimal ? value * 100 : value;
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted currency
 */
export function formatCurrency(amount, currency = 'USD') {
  if (amount === null || amount === undefined || isNaN(amount)) return '$0';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format date to readable string
 * @param {Date|string} date - Date to format
 * @param {string} format - Format type ('short', 'long', 'time')
 * @returns {string} Formatted date
 */
export function formatDate(date, format = 'short') {
  if (!date) return '—';

  // Handle GA4 date format (YYYYMMDD)
  let d;
  if (typeof date === 'string' && date.length === 8 && !date.includes('-')) {
    // Parse YYYYMMDD format
    const year = date.substring(0, 4);
    const month = date.substring(4, 6);
    const day = date.substring(6, 8);
    d = new Date(`${year}-${month}-${day}`);
  } else {
    d = new Date(date);
  }

  if (isNaN(d.getTime())) return '—';

  switch (format) {
    case 'short':
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'long':
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    case 'time':
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    default:
      return d.toLocaleDateString();
  }
}

/**
 * Merge Firebase and GA4 data by date
 * @param {Array} firebaseData - Firebase analytics data
 * @param {Array} ga4Data - GA4 analytics data
 * @returns {Array} Merged data array
 */
export function mergeAnalyticsData(firebaseData, ga4Data) {
  const dataMap = new Map();

  // Add Firebase data
  firebaseData?.forEach(item => {
    const dateKey = item.date || item.timestamp;
    dataMap.set(dateKey, { ...item, date: dateKey });
  });

  // Merge GA4 data
  ga4Data?.forEach(item => {
    const dateKey = item.date;
    const existing = dataMap.get(dateKey) || { date: dateKey };
    dataMap.set(dateKey, { ...existing, ...item });
  });

  // Convert to array and sort by date
  return Array.from(dataMap.values()).sort((a, b) =>
    new Date(a.date) - new Date(b.date)
  );
}

/**
 * Parse GA4 date format (YYYYMMDD) to Date object
 * @param {string} dateStr - GA4 date string
 * @returns {Date} Date object
 */
export function parseGA4Date(dateStr) {
  if (!dateStr || dateStr.length !== 8) return new Date();

  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6)) - 1;
  const day = parseInt(dateStr.substring(6, 8));

  return new Date(year, month, day);
}

/**
 * Calculate date range in days
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of days
 */
export function calculateDateRange(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Get date range presets
 * @param {string} preset - Preset name ('7d', '30d', '90d', 'mtd', 'lastMonth')
 * @returns {Object} { startDate, endDate }
 */
export function getDateRangePreset(preset) {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  let startDate;

  switch (preset) {
    case '7d':
      startDate = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
      break;
    case '30d':
      startDate = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
      break;
    case '90d':
      startDate = new Date(today.setDate(today.getDate() - 90)).toISOString().split('T')[0];
      break;
    case 'mtd':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      break;
    case 'lastMonth':
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      startDate = lastMonth.toISOString().split('T')[0];
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate,
        endDate: lastDayOfMonth.toISOString().split('T')[0]
      };
    default:
      startDate = new Date(today.setDate(today.getDate() - 30)).toISOString().split('T')[0];
  }

  return { startDate, endDate };
}

/**
 * Calculate engagement rate
 * @param {number} engagedSessions - Number of engaged sessions
 * @param {number} totalSessions - Total sessions
 * @returns {number} Engagement rate (0-1)
 */
export function calculateEngagementRate(engagedSessions, totalSessions) {
  if (!totalSessions || totalSessions === 0) return 0;
  return engagedSessions / totalSessions;
}

/**
 * Export data to CSV
 * @param {Array} data - Data array
 * @param {string} filename - Output filename
 */
export function exportToCSV(data, filename = 'export.csv') {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Group data by time period
 * @param {Array} data - Data array with date field
 * @param {string} period - Period type ('hour', 'day', 'week', 'month')
 * @returns {Array} Grouped data
 */
export function groupByTimePeriod(data, period = 'day') {
  const grouped = {};

  data.forEach(item => {
    const date = new Date(item.date);
    let key;

    switch (period) {
      case 'hour':
        key = `${date.toISOString().split('T')[0]} ${date.getHours()}:00`;
        break;
      case 'day':
        key = date.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
        break;
      case 'month':
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        break;
      default:
        key = date.toISOString().split('T')[0];
    }

    if (!grouped[key]) {
      grouped[key] = { date: key, values: [] };
    }
    grouped[key].values.push(item);
  });

  return Object.values(grouped);
}

/**
 * Calculate trend direction
 * @param {Array} values - Array of numeric values (chronological order)
 * @returns {string} 'up', 'down', or 'stable'
 */
export function calculateTrend(values) {
  if (!values || values.length < 2) return 'stable';

  const firstHalf = values.slice(0, Math.floor(values.length / 2));
  const secondHalf = values.slice(Math.floor(values.length / 2));

  const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;

  const diff = secondAvg - firstAvg;
  const threshold = firstAvg * 0.05; // 5% threshold

  if (diff > threshold) return 'up';
  if (diff < -threshold) return 'down';
  return 'stable';
}

/**
 * Get color for chart by index
 * @param {number} index - Chart color index
 * @returns {string} Color hex code
 */
export function getChartColor(index) {
  const colors = [
    '#3B82F6', // chart-1
    '#8B5CF6', // chart-2
    '#EC4899', // chart-3
    '#F59E0B', // chart-4
    '#10B981', // chart-5
    '#06B6D4'  // chart-6
  ];

  return colors[index % colors.length];
}
